/* Copyright (c) 2023 Xperi. All Rights Reserved. */

import { AnyAction } from 'redux';

import { IDLE_USER_THRESHOLD_TIME } from '../../config';
import { DeviceModel } from '../../core/device/DeviceModel';
import { PCSettings } from '../../core/entities';
import { PCState } from '../../core/enums';
import { ServicesClientImpl } from '../../core/interactors';
import { ScreenTaskControllerTypes } from '../../core/navigation';
import { ScreenControllerTypes } from '../../core/navigation/ScreenControllerTypes';
import type { TGetState } from '../../core/tivo/tivo-redux-wrapper';
import { setPCPinOverlayProps } from '../overlay/pcpinoverlay/pcpinoverlay.actions';
import {
    GLOBAL_MOUSE_TRAP,
    onKeyBackPcPinOverlay,
    onKeyDigitPcPinOverlay,
    onKeyDownPcPinOverlay,
    onKeyLeftPcPinOverlay,
    onKeyOkPcPinOverlay,
    onKeyRightPcPinOverlay,
    onKeyUpPcPinOverlay,
    onMouseClickPcPinOverlay,
    onMouseOutPcPinOverlay,
    onMouseOverPcPinOverlay,
    showEnterPCPinOverlay,
    updatePcState,
} from '../overlay/pcpinoverlay/pcpinoverlay.reducers';
import { hideOverlayDialog } from '../overlaydialog/overlaydialog.reducers';
import {
    setScreenController,
    setScreenTaskController,
    showDefaultScreen,
    showGuideScreen,
} from '../screens/screens.reducers';
import { PlaybackState } from '../videoplayerui/infobanner/PlaybackTypes';
import {
    hideAntiburnOsdAction,
    resetAntiburnTimeoutAction,
    showAntiburnOsdAction,
} from './antiburnosd.actions';
import {
    HIDE_ANTIBURN_OSD,
    SET_PARENTAL_CHECK_ON_ANTIBURN_OSD,
    SHOW_ANTIBURN_OSD,
    UPDATE_ANTIBURN_TIMER,
} from './antiburnosd.types';

const initialState: {
    isActive: boolean;
    antiburnTimer: any;
    shouldDoParentalCheck: boolean;
} = {
    isActive: false,
    antiburnTimer: null,
    shouldDoParentalCheck: false,
};

const antiburnOsdReducer = (state = initialState, { type, payload } = <AnyAction>{}) => {
    switch (type) {
        case SHOW_ANTIBURN_OSD:
        case HIDE_ANTIBURN_OSD:
            return {
                ...state,
                isActive: payload.isActive,
            };

        case UPDATE_ANTIBURN_TIMER:
            return {
                ...state,
                antiburnTimer: payload.antiburnTimer,
            };

        case SET_PARENTAL_CHECK_ON_ANTIBURN_OSD:
            return {
                ...state,
                shouldDoParentalCheck: payload.shouldDoParentalCheck,
            };

        default:
            return state;
    }
};

const setPreviousScreenAndHideAntiburnOsd =
    (taskController: string) => (dispatch: Function, getState: TGetState) => {
        const { previousScreenTaskController, previousScreenController } = getState().screens;
        dispatch(setScreenController(previousScreenController));
        dispatch(setScreenTaskController(taskController || previousScreenTaskController));
        dispatch(hideAntiburnOsdAction());
    };

export const hideAntiburnOsd = () => (dispatch: Function, getState: TGetState) => {
    const {
        antiburnOsd: { shouldDoParentalCheck },
        screens: { previousScreenTaskController },
    } = getState();

    if (shouldDoParentalCheck) {
        dispatch(setPCPinOverlayProps({ dimmerOpacity: 1 }));
        dispatch(
            showEnterPCPinOverlay(() => {
                dispatch(setPreviousScreenAndHideAntiburnOsd(previousScreenTaskController));
            }),
        );
    } else dispatch(setPreviousScreenAndHideAntiburnOsd(previousScreenTaskController));
};

export const showAntiburnOsd = () => (dispatch: Function, getState: Function) => {
    const { isOverlayDialogActive } = getState().overlayDialog;
    if (isOverlayDialogActive) dispatch(hideOverlayDialog());
    dispatch(setScreenController(ScreenControllerTypes.ANTIBURN_SCREEN));
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DEFAULT_ANTIBURN_OSD_TASKS));
    dispatch(showAntiburnOsdAction());
};

export const resetAntiburnTimeout = () => (dispatch: Function, getState: Function) => {
    const { screenController } = getState().screens;
    const { antiburnTimer } = getState().antiburnOsd;
    const { playbackState } = getState().videoPlayer;
    const isPlaybackStatePaused =
        playbackState === PlaybackState.PAUSE || playbackState === PlaybackState.STOP;
    clearTimeout(antiburnTimer);
    if (
        screenController !== ScreenControllerTypes.ANTIBURN_SCREEN &&
        screenController !== ScreenControllerTypes.VIDEO_PLAYER
    ) {
        const timer = setTimeout(() => {
            dispatch(showAntiburnOsd());
        }, IDLE_USER_THRESHOLD_TIME);
        dispatch(resetAntiburnTimeoutAction(timer));
    }

    if (screenController === ScreenControllerTypes.VIDEO_PLAYER && !isPlaybackStatePaused) {
        DeviceModel.getTVProperties().disableScreenSaver();
    } else {
        DeviceModel.getTVProperties().enableScreenSaver();
    }
};

/**
 * Below reducers are related to operations on key events.
 */
export const onKeyBackAntiburnOsd = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(hideAntiburnOsdAction());
        dispatch(onKeyBackPcPinOverlay());
        dispatch(showDefaultScreen());
    } else dispatch(hideAntiburnOsd());
};

export const onKeyGuideAntiburn = () => (dispatch: Function) => {
    dispatch(hideAntiburnOsdAction());
    dispatch(showGuideScreen());
};

export const onKeyOkAntiburnOsd = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyOkPcPinOverlay());
    else dispatch(hideAntiburnOsd());
};

export const onKeyUpAntiburnOsd = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyUpPcPinOverlay());
};

export const onKeyDownAntiburnOsd = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyDownPcPinOverlay());
};

export const onKeyLeftAntiburnOsd = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyLeftPcPinOverlay());
};

export const onKeyRightAntiburnOsd = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyRightPcPinOverlay());
};

export const onKeyDigitAntiburnOsd = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onKeyDigitPcPinOverlay());
};

export const onMouseOverAntiburnOsd = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onMouseOverPcPinOverlay());
    else {
        dispatch(hideAntiburnOsd());
        dispatch(resetAntiburnTimeout());
    }
};

export const onMouseOutAntiburnOsd = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK)
        dispatch(onMouseOutPcPinOverlay());
};

export const onMouseClickAntiburnOsd = () => (dispatch: Function, getState: TGetState) => {
    const { screenTaskController } = getState().screens;
    const { value } = getState().inputFactory;

    if (screenTaskController === ScreenTaskControllerTypes.PC_PIN_OVERLAY_TASK) {
        dispatch(onMouseClickPcPinOverlay());

        if (value === GLOBAL_MOUSE_TRAP) {
            dispatch(hideAntiburnOsdAction());
            dispatch(showDefaultScreen());
        }
    }
};

export default antiburnOsdReducer;
